import { APPLICATION_STEPS, QUOTE_FLOW_SECTIONS } from 'common/constants';
import { handleFullStoryEvent } from 'helpers/utils';
import { toast } from 'react-toastify';
import { createSliceDefinitions } from 'store/helpers/createSliceDefinitions';
import type { SliceCreator } from 'store/useAppStore.types';
import type { QuoteStepperSlice, QuoteStepperSliceState } from './quoteStepper.types';
import { findEligibleStep } from './quoteStepper.utils';

const { actions } = createSliceDefinitions('QuoteStepperSlice', {
  UPDATE_STEPPER: 'updateStepper',
  SET_SECTION: 'setSection',
  SET_CURRENT_STEP: 'setCurrentStep',
  RESET: 'reset',
});

const initialState: QuoteStepperSliceState = {
  currentSection: 0,
  currentStep: 0,
  visitedSteps: [],
};

export const createQuoteStepperSlice: SliceCreator<QuoteStepperSlice> = (set, get) => ({
  ...initialState,

  actions: {
    updateStepper: ({ destination, submissionId, stepsSkipConditions }) => {
      const actionDefinition = {
        type: actions.UPDATE_STEPPER,
        destination,
      };

      const { currentSection, currentStep } = get().quoteStepper;
      const currentSectionSteps = QUOTE_FLOW_SECTIONS[currentSection].steps || [];

      if (destination === 'next') {
        set(
          (state) => {
            // Switch to the next section from the last step in `currentSection`
            if (currentStep + 1 > currentSectionSteps.length - 1) {
              state.quoteStepper.currentSection += 1;
              state.quoteStepper.currentStep = 0;
            } else {
              const { stepChangeCount } = findEligibleStep({
                submissionId,
                currentSectionSteps,
                currentStep,
                destination,
                stepsSkipConditions,
              });

              state.quoteStepper.currentStep += stepChangeCount;
            }

            // Check if step has been visited before
            if (state.quoteStepper.currentSection === 0) {
              const applicationStepsArray = Object.values(APPLICATION_STEPS);
              const newStepIndex = state.quoteStepper.currentStep;
              const stepAtIndex = applicationStepsArray[newStepIndex];

              if (stepAtIndex) {
                const stepKey = stepAtIndex.key;

                // Track FullStory event if step has not been visited before
                // Prevents duplicate events from being tracked
                if (!state.quoteStepper.visitedSteps.includes(stepKey)) {
                  state.quoteStepper.visitedSteps.push(stepKey);
                  handleFullStoryEvent(stepKey);
                }
              }
            }
          },
          false,
          actionDefinition,
        );
      } else {
        set(
          (state) => {
            const { stepChangeCount } = findEligibleStep({
              submissionId,
              currentSectionSteps,
              currentStep,
              destination,
              stepsSkipConditions,
            });

            state.quoteStepper.currentStep -= stepChangeCount;
          },
          false,
          actionDefinition,
        );
      }

      // Scroll to top on each step change
      window?.scrollTo(0, 0);
      // Dismiss all toast messages on each step change
      toast?.dismiss();
    },
    setSection: (payload) => {
      set(
        (state) => {
          state.quoteStepper.currentSection = payload;
          state.quoteStepper.currentStep = 0;
        },
        false,
        { type: actions.SET_SECTION, payload },
      );

      // Scroll to top on each section change
      window?.scrollTo(0, 0);
    },
    setCurrentStep: (payload) => {
      set(
        (state) => {
          state.quoteStepper.currentStep = payload;
        },
        false,
        { type: actions.SET_CURRENT_STEP, payload },
      );

      // Scroll to top on step change
      window?.scrollTo(0, 0);
      // Dismiss all toast messages on step change
      toast?.dismiss();
    },
    reset: () => {
      set(
        (state) => {
          state.quoteStepper = {
            ...state.quoteStepper,
            ...initialState,
          };
        },
        false,
        actions.RESET,
      );
    },
  },
});
